import {
  ProgressInfo,
  UpdateDownloadedEvent,
  UpdateInfo,
} from 'electron-updater'
import { useCallback, useEffect, useRef, useState } from 'react'
import { isElectron } from '~/utils/common'

export interface ElectronUpdaterHookResult {
  availableVersion: string | null
  statusMessage: string
  isUpdateAvailable: boolean
  isUpdateDownloaded: boolean
  isChecking: boolean
  error: string | null
  checkForUpdates: () => void
  quitAndInstall: () => void
}

export const useElectronUpdater = (
  checkIntervalMs?: number,
): ElectronUpdaterHookResult => {
  const [statusMessage, setStatusMessage] = useState<string>('')
  const [isUpdateAvailable, setIsUpdateAvailable] = useState<boolean>(false)
  const [isUpdateDownloaded, setIsUpdateDownloaded] = useState<boolean>(false)
  const [isChecking, setIsChecking] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [availableVersion, setAvailableVersion] = useState<string | null>(null)

  const messageTimerRef = useRef<NodeJS.Timeout | null>(null)
  const intervalTimerRef = useRef<NodeJS.Timeout | null>(null)

  const clearMessageTimer = () => {
    if (messageTimerRef.current) {
      clearTimeout(messageTimerRef.current)
      messageTimerRef.current = null
    }
  }

  const resetStatusAfterDelay = (delay = 8000) => {
    clearMessageTimer()
    messageTimerRef.current = setTimeout(() => {
      if (!isUpdateDownloaded && !isChecking) {
        setStatusMessage('')
        setError(null)
      }
    }, delay)
  }

  const checkForUpdates = useCallback(() => {
    if (isElectron && !isChecking) {
      clearMessageTimer()
      setError(null)
      setStatusMessage('')
      setIsUpdateAvailable(false)
      setIsUpdateDownloaded(false)
      window.electron?.updates.check()
    }
  }, [isElectron, isChecking])

  const quitAndInstall = useCallback(() => {
    if (isElectron && isUpdateDownloaded) {
      window.electron?.updates.quitAndInstall()
    }
  }, [isElectron, isUpdateDownloaded])

  // Listener setup
  useEffect(() => {
    if (!isElectron) {
      return
    }

    const handleUpdateChecking = () => {
      clearMessageTimer()
      setError(null)
      setStatusMessage('Checking for updates...')
      setIsChecking(true)
      setIsUpdateAvailable(false)
      setIsUpdateDownloaded(false)
      setAvailableVersion(null)
    }

    const handleUpdateAvailable = (info: UpdateInfo) => {
      clearMessageTimer()
      setError(null)
      setStatusMessage(`Version ${info.version} is available`)
      setIsChecking(true)
      setIsUpdateAvailable(true)
      setIsUpdateDownloaded(false)
      setAvailableVersion(info.version)
    }

    const handleUpdateNotAvailable = (_info: UpdateInfo) => {
      setError(null)
      setStatusMessage('App is up-to-date')
      setIsChecking(false)
      setIsUpdateAvailable(false)
      setIsUpdateDownloaded(false)
      setAvailableVersion(null)
      resetStatusAfterDelay()
    }

    const handleUpdateError = (err: Error, message?: string) => {
      console.error('Electron Update Error:', message || err.message, err)
      const errorMessage = message || err.message || 'Unknown update error'
      setError(errorMessage)
      setStatusMessage('Update check failed')
      setIsChecking(false)
      setIsUpdateAvailable(false)
      setIsUpdateDownloaded(false)
      setAvailableVersion(null)
      resetStatusAfterDelay()
    }

    const handleDownloadProgress = (progress: ProgressInfo) => {
      clearMessageTimer()
      setError(null)
      setStatusMessage(`Downloading... ${progress.percent.toFixed(0)}%`)
      setIsChecking(true)
      setIsUpdateAvailable(true)
      setIsUpdateDownloaded(false)
    }

    const handleUpdateDownloaded = (event: UpdateDownloadedEvent) => {
      clearMessageTimer()
      setError(null)
      setStatusMessage(`Version ${event.version} downloaded`)
      setIsChecking(false)
      setIsUpdateAvailable(true)
      setIsUpdateDownloaded(true)
      setAvailableVersion(event.version)
    }

    window.electron?.receive('update-checking', handleUpdateChecking)
    window.electron?.receive('update-available', handleUpdateAvailable)
    window.electron?.receive('update-not-available', handleUpdateNotAvailable)
    window.electron?.receive('update-error', handleUpdateError)
    window.electron?.receive('update-download-progress', handleDownloadProgress)
    window.electron?.receive('update-downloaded', handleUpdateDownloaded)

    return () => {
      window.electron?.removeAllListeners('update-checking')
      window.electron?.removeAllListeners('update-available')
      window.electron?.removeAllListeners('update-not-available')
      window.electron?.removeAllListeners('update-error')
      window.electron?.removeAllListeners('update-download-progress')
      window.electron?.removeAllListeners('update-downloaded')
      clearMessageTimer()
    }
  }, [isElectron])

  // Interval check
  useEffect(() => {
    const clearExistingInterval = () => {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current)
        intervalTimerRef.current = null // Reset the ref
      }
    }

    if (isElectron && checkIntervalMs && checkIntervalMs > 0) {
      console.log(
        `⏲️ Setting up ⚡️ update check every ${checkIntervalMs / 60 / 60 / 1000} hours`,
      )
      intervalTimerRef.current = setInterval(() => {
        checkForUpdates()
      }, checkIntervalMs)

      return () => {
        console.log('Clearing update check interval.')
        clearExistingInterval()
      }
    } else {
      console.log(
        'Update check interval condition not met, ensuring no interval is running.',
      )
      clearExistingInterval()
      return undefined
    }
  }, [isElectron, checkIntervalMs, checkForUpdates])

  return {
    availableVersion,
    statusMessage,
    isUpdateAvailable,
    isUpdateDownloaded,
    isChecking,
    error,
    checkForUpdates,
    quitAndInstall,
  }
}
